import React, { useState, useEffect } from 'react';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  getDFUInformation,
  updateAppVersionData,
} from '../../../repository/DFUDAO';
import { useUserContext } from '../../../context_providers/user_context/UserProvider';
import EditFirmwareVersioningModal from './modals/EditFirmwareVersioningModal';
import Loading from '../../../components/loading/Loading';
import LoadingSpinner from '../../../components/loading/LoadingSpinner';
import { useToast } from '../../../context_providers/toast_context/ToastContext';
import { ToastType } from '../../../context_providers/toast_context/ToastContext';
import COLORS from '../../../resources/style/Themes';
import PageHeader from '../../../components/utils/PageHeader';
import EditIcon from '@mui/icons-material/Edit';
import ErrorComponent from '../../../components/utils/ErrorComponent';
import APIErrorCodes from '../../../utils/error_codes/APIErrorCodes';
import { FirmwareLookupTableUpdate } from '../../../../generated-client/models';

type FirmwareKeys = keyof FirmwareLookupTableUpdate;

export const FirmwareVersioning = () => {
  const [responseData, setResponseData] = useState<FirmwareLookupTableUpdate>();
  const { bearerToken } = useUserContext();
  const [showVersioningModal, setShowVersioningModal] = useState(false);
  const [apiError, setApiError] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchDfuInformation = async () => {
      try {
        const response = await getDFUInformation();
        setResponseData(response);
      } catch (error) {
        setApiError(true);
        // console.log(error);
      }
    };

    fetchDfuInformation();
  }, [bearerToken]);

  const editVersioning = async (appVersionData: FirmwareLookupTableUpdate) => {
    setShowVersioningModal(false);
    try {
      await updateAppVersionData(appVersionData);
      showToast(`Successfully updated app versioning.`, ToastType.SUCCESS);
      setResponseData((prevData) => ({
        ...prevData,
        ...appVersionData,
      }));
    } catch (e) {
      showToast(
        `Failed to update App Versioning Data. Please try again.`,
        ToastType.DANGER,
      );
    }
  };

  const convertToProperCase = (str: string) => {
    let result = str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
    result = result.replace('Ios', 'iOS');
    return result;
  };

  const renderData = () => {
    return (
      <>
        <PageHeader
          disabled={false}
          action={() => setShowVersioningModal(true)}
          icon={<EditIcon style={{ color: COLORS.WHITE }} />}
          header={'Device Firmware Versioning'}
          label={'Edit Versioning'}
        />
        <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 0 }}>
          <Table>
            <TableBody>
              {(
                [
                  'latest_app_version_android',
                  'latest_app_version_ios',
                  'latest_firmware_version',
                  'minimum_app_version_android',
                  'minimum_app_version_ios',
                  'current_manufacturing_firmware_version',
                ] as FirmwareKeys[]
              ).map((key, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    <Typography fontWeight="bold">
                      {convertToProperCase(key)}
                    </Typography>
                  </TableCell>
                  <TableCell>{responseData?.[key]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <>
      {apiError ? (
        <ErrorComponent error={APIErrorCodes.FETCH_ERROR} />
      ) : (
        <>
          <Container maxWidth="xl" sx={{ paddingBottom: '100px' }}>
            {responseData ? renderData() : <LoadingSpinner />}
          </Container>

          <EditFirmwareVersioningModal
            show={showVersioningModal}
            onHide={() => setShowVersioningModal(false)}
            versioning={responseData}
            saveChanges={editVersioning}
          />
        </>
      )}
    </>
  );
};

export default withAuthenticationRequired(FirmwareVersioning, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
